import { asyncRoutes } from '@/router'
import constantRoutes from '@/router/routes/constant-routes'

const SALT_ROTATED_KEY = 'permission_salt_rotated'
const MERCHANT_ID_ERROR_KEY = 'permission_merchant_id_error'

const getFlagFromStorage = (storageKey) => {
  return Boolean(sessionStorage.getItem(storageKey))
}
/**
 * Use meta.permissions to determine if the current user has permission
 * @param permissions
 * @param route
 */
function hasPermission(permissions, route) {
  if (route.meta && route.meta.permissions) {
    return permissions.some((permission) =>
      route.meta.permissions.includes(permission.code)
    )
  } else {
    return true
  }
}

function isRouteDisabled(route) {
  if (typeof route.disabled === 'function') {
    return route.disabled()
  }

  return !!route.disabled
}

/**
 * Filter asynchronous routing tables by recursion
 * @param routes asyncRoutes
 * @param permissions
 */
export function filterAsyncRoutes(routes, permissions) {
  const res = []

  routes.forEach((route) => {
    const tmp = { ...route }
    if (hasPermission(permissions, tmp) && !isRouteDisabled(route)) {
      if (tmp.children) {
        tmp.children = filterAsyncRoutes(tmp.children, permissions)
      }
      res.push(tmp)
    }
  })

  return res
}

const state = {
  routes: [...constantRoutes],
  addRoutes: [],
  addedRoutes: false,
  sessionExpired: false,
  saltRotated: getFlagFromStorage(SALT_ROTATED_KEY),
  merchantIdError: getFlagFromStorage(MERCHANT_ID_ERROR_KEY),
}

const getters = {
  sessionExpired: (state) => state.sessionExpired,
  saltRotated: (state) => state.saltRotated,
  merchantIdError: (state) => state.merchantIdError,
}

const mutations = {
  SET_ROUTES: (state, routes) => {
    state.addedRoutes = true
    state.addRoutes = routes
    state.routes = state.routes.concat(routes)
  },
  RESET_ROUTES: (state) => {
    state.routes = [...constantRoutes]
    state.addRoutes = []
    state.addedRoutes = false
  },
  SET_SESSION_EXPIRED: (state, value) => {
    state.sessionExpired = value
  },
  SET_SALT_ROTATED: (state, value) => {
    state.saltRotated = value
    try {
      if (value) {
        sessionStorage.setItem(SALT_ROTATED_KEY, 'true')
      } else {
        sessionStorage.removeItem(SALT_ROTATED_KEY)
      }
    } catch {
      // Silently handle sessionStorage errors
    }
  },
  SET_MERCHANT_ID_ERROR: (state, value) => {
    state.merchantIdError = value
    try {
      if (value) {
        sessionStorage.setItem(MERCHANT_ID_ERROR_KEY, 'true')
      } else {
        sessionStorage.removeItem(MERCHANT_ID_ERROR_KEY)
      }
    } catch {
      // Silently handle sessionStorage errors
    }
  },
}

const actions = {
  generateRoutes({ commit }, permissions) {
    return new Promise((resolve) => {
      const accessedRoutes = filterAsyncRoutes(asyncRoutes, permissions)
      commit('SET_ROUTES', accessedRoutes)
      resolve(accessedRoutes)
    })
  },
  resetRoutes({ commit }) {
    commit('RESET_ROUTES')
  },
  setSessionExpired({ commit, dispatch }) {
    commit('SET_SESSION_EXPIRED', '')

    return true
  },
  setSaltRotated({ commit }, value) {
    // Flag is persisted in sessionStorage to survive page reloads
    commit('SET_SALT_ROTATED', value)
  },
  setMerchantIdError({ commit }, value) {
    // Flag is persisted in sessionStorage to survive page reloads
    commit('SET_MERCHANT_ID_ERROR', value)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
